/**
 * FBL Prime badge — Silver / Gold / Diamond member + free MRP left in the
 * member's CURRENT monthly cycle (Shailendra 2026-10-03). Lets an agent pitch
 * "sir, you still have ₹X of free products this month — add them to this order".
 *
 * Shape of `prime` (from GET /api/ai/prime/badges or the cohort dashboard row):
 *   { tier, label, monthlyCap, usedThisCycle, remainingThisCycle, minOrder,
 *     cycleStart, nextResetAt, expiresAt }
 * Non-members have no badge at all — every component here renders nothing for them.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const AI_PATH = String(process.env.REACT_APP_AI_PATH || '').trim();

export const PRIME_STYLE = {
  silver:  { rowBg: '#eceff1', chipBg: '#cfd8dc', fg: '#37474f', border: '#b0bec5', icon: '🥈' },
  gold:    { rowBg: '#fff3cd', chipBg: '#ffd966', fg: '#7a5800', border: '#f1c232', icon: '🥇' },
  diamond: { rowBg: '#a9a9a9', chipBg: '#424242', fg: '#ffffff', border: '#212121', icon: '💎' },
};

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '');
const p10 = (p) => String(p || '').replace(/\D/g, '').slice(-10);

// Background for a whole table row (seller list). undefined for non-members.
export const primeRowBg = (prime) => (prime && PRIME_STYLE[prime.tier] ? PRIME_STYLE[prime.tier].rowBg : undefined);

export function PrimeTierChip({ prime, size = 'sm' }) {
  if (!prime || !PRIME_STYLE[prime.tier]) return null;
  const st = PRIME_STYLE[prime.tier];
  const big = size === 'lg';
  return (
    <span
      title={`FBL Prime ${prime.label} — ${inr(prime.monthlyCap)} free MRP / month on orders of ${inr(prime.minOrder)}+`}
      style={{
        display: 'inline-block', whiteSpace: 'nowrap', fontWeight: 800,
        fontSize: big ? 12 : 10, padding: big ? '3px 10px' : '2px 8px', borderRadius: 999,
        background: st.chipBg, color: st.fg, border: `1px solid ${st.border}`,
      }}
    >
      {st.icon} {prime.label} Member
    </span>
  );
}

export function PrimeMrpLeft({ prime }) {
  if (!prime) return null;
  const left = Number(prime.remainingThisCycle) || 0;
  return (
    <span
      title={`Used ${inr(prime.usedThisCycle)} of ${inr(prime.monthlyCap)} this cycle · resets ${fmtDay(prime.nextResetAt)}`}
      style={{ fontWeight: 700, color: left > 0 ? '#0f7a37' : '#9e9e9e', whiteSpace: 'nowrap' }}
    >
      {inr(left)}
      <span style={{ display: 'block', fontSize: 9, fontWeight: 500, color: '#888' }}>
        of {inr(prime.monthlyCap)} · resets {fmtDay(prime.nextResetAt)}
      </span>
    </span>
  );
}

// Batch lookup for a list of phones → { [10-digit phone]: prime }. Members only.
export function usePrimeBadges(phones) {
  const key = [...new Set((phones || []).map(p10).filter(p => p.length === 10))].sort().join(',');
  const [badges, setBadges] = useState({});
  useEffect(() => {
    if (!key) { setBadges({}); return undefined; }
    let live = true;
    axios.get(`${AI_PATH}/api/ai/prime/badges`, { params: { phones: key } })
      .then((r) => { if (live) setBadges(r.data?.badges || {}); })
      .catch(() => { if (live) setBadges({}); });
    return () => { live = false; };
  }, [key]);
  return badges;
}

export const primeFor = (badges, phone) => (badges && badges[p10(phone)]) || null;

// Dialer cockpit banner — shown only when the buyer on the line is a member.
export function PrimeDialerBanner({ phone }) {
  const prime = primeFor(usePrimeBadges(phone ? [phone] : []), phone);
  if (!prime || !PRIME_STYLE[prime.tier]) return null;
  const st = PRIME_STYLE[prime.tier];
  const left = Number(prime.remainingThisCycle) || 0;
  return (
    <div style={{
      margin: '8px 12px 0', padding: '8px 12px', borderRadius: 8,
      background: st.rowBg, border: `2px solid ${st.border}`,
      color: prime.tier === 'diamond' ? '#111' : st.fg,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <PrimeTierChip prime={prime} size="lg" />
        <span style={{ fontSize: 10, opacity: 0.85 }}>resets {fmtDay(prime.nextResetAt)}</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 13, fontWeight: 800 }}>
        {left > 0
          ? <>Free MRP left this month: <span style={{ color: prime.tier === 'diamond' ? '#004d1a' : '#0f7a37' }}>{inr(left)}</span></>
          : <>This month's free MRP fully used</>}
      </div>
      <div style={{ fontSize: 11, marginTop: 2 }}>
        {left > 0
          ? `Pitch: "Sir, aapke ${inr(left)} ke free products is mahine baaki hain — ${inr(prime.minOrder)}+ ke order par add kar sakte hain."`
          : `Used ${inr(prime.usedThisCycle)} of ${inr(prime.monthlyCap)} — refills on ${fmtDay(prime.nextResetAt)}.`}
      </div>
    </div>
  );
}
